import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Loader2, TrendingUp, Target, Check, X } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";

const SERIES = [
  { key: "high_ticket", name: "High-Ticket Revenue", color: "#C5A059", fill: 0.15, money: true, axis: "money" },
  { key: "vault", name: "Language Vault Revenue", color: "#98A898", fill: 0.1, money: true, axis: "money" },
  { key: "contacts", name: "Total Contacts", color: "#A98B63", fill: 0.12, money: false, axis: "count" },
];

const HORIZONS = [
  { id: "all", label: "All Time" },
  { id: "ytd", label: "Year to Date" },
  { id: "t12m", label: "Trailing 12 Months" },
];

const VIEWS = [
  { id: "cumulative", label: "Cumulative" },
  { id: "monthly", label: "Monthly" },
];

const REVENUE_COLOR = "#C5A059";
const SUBS_COLOR = "#B07C83";

const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const usdFromDollars = (d) => `$${(d || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const CumulativeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div data-testid="growth-tooltip" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)" }}
      className="rounded-xl px-4 py-3 shadow-lg ring-1 ring-black/5">
      <p className="font-display text-base text-ink">{label}</p>
      {payload.map((p) => {
        const s = SERIES.find((x) => x.key === p.dataKey);
        if (!s) return null;
        return (
          <p key={p.dataKey} className="mt-1 flex items-center gap-2 text-sm text-ink-soft">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.stroke }} />
            {s.name}: <span className="font-medium text-ink">{s.money ? usdFromDollars(p.value) : p.value}</span>
          </p>
        );
      })}
    </div>
  );
};

const MonthlyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rev = payload.find((p) => p.dataKey === "revenue");
  const subs = payload.find((p) => p.dataKey === "subscribers");
  return (
    <div data-testid="growth-tooltip" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)" }}
      className="rounded-xl px-4 py-3 shadow-lg ring-1 ring-black/5">
      <p className="font-display text-base text-ink">{label}</p>
      {rev && (
        <p className="mt-1 flex items-center gap-2 text-sm text-ink-soft">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: REVENUE_COLOR }} />
          Revenue this month: <span className="font-medium text-ink">{usdFromDollars(rev.value)}</span>
        </p>
      )}
      {subs && (
        <p className="mt-1 flex items-center gap-2 text-sm text-ink-soft">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: SUBS_COLOR }} />
          Active subscribers: <span className="font-medium text-ink">{subs.value}</span>
        </p>
      )}
    </div>
  );
};

export default function CumulativeGrowthChart({ token, onAuthError }) {
  const [horizon, setHorizon] = useState("all");
  const [view, setView] = useState("cumulative");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);

  const [goals, setGoals] = useState({ revenue_goal_cents: 0, subscriber_goal: 0 });
  const [editing, setEditing] = useState(false);
  const [draftRevenue, setDraftRevenue] = useState("");
  const [draftSubs, setDraftSubs] = useState("");
  const [savingGoals, setSavingGoals] = useState(false);
  const [goalError, setGoalError] = useState("");

  const load = useCallback(async (h) => {
    setLoading(true); setError(""); setExpired(false);
    try {
      const { data } = await axios.get(`${API}/admin/cumulative`, { params: { horizon: h }, headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) {
      setData(null);
      if (e.response?.status === 401) { setExpired(true); setError("Your admin session expired — please sign in again."); }
      else { setError("Couldn't load growth data. Please try again."); }
    } finally { setLoading(false); }
  }, [token]);

  const syncDrafts = useCallback((g) => {
    setDraftRevenue(g.revenue_goal_cents ? String(g.revenue_goal_cents / 100) : "");
    setDraftSubs(g.subscriber_goal ? String(g.subscriber_goal) : "");
  }, []);

  const loadGoals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/growth-goals`, { headers: { Authorization: `Bearer ${token}` } });
      setGoals(data);
      syncDrafts(data);
    } catch (e) { if (e.response?.status === 401) onAuthError?.(); }
  }, [token, syncDrafts, onAuthError]);

  useEffect(() => { if (token) load(horizon); }, [token, horizon, load]);
  useEffect(() => { if (token) loadGoals(); }, [token, loadGoals]);

  const saveGoals = async () => {
    setSavingGoals(true); setGoalError("");
    try {
      const body = {
        revenue_goal_cents: Math.round((parseFloat(draftRevenue) || 0) * 100),
        subscriber_goal: Math.round(parseInt(draftSubs, 10) || 0),
      };
      const { data } = await axios.put(`${API}/admin/growth-goals`, body, { headers: { Authorization: `Bearer ${token}` } });
      setGoals({ revenue_goal_cents: data.revenue_goal_cents, subscriber_goal: data.subscriber_goal });
      setEditing(false);
    } catch (e) {
      if (e.response?.status === 401) onAuthError?.();
      else setGoalError("Couldn't save goals. Please try again.");
    } finally { setSavingGoals(false); }
  };

  const cancelGoals = () => { syncDrafts(goals); setGoalError(""); setEditing(false); };

  const raw = data?.points || [];
  const cumPoints = raw.map((p) => ({ label: p.label, high_ticket: p.high_ticket_cents / 100, vault: p.vault_cents / 100, contacts: p.contacts }));
  const monthlyPoints = raw.map((p) => ({ label: p.label, revenue: p.revenue_month_cents / 100, subscribers: p.active_subs }));

  const revGoalDollars = goals.revenue_goal_cents / 100;
  const subGoal = goals.subscriber_goal;
  const revReached = revGoalDollars > 0 && monthlyPoints.some((p) => p.revenue >= revGoalDollars);
  const subReached = subGoal > 0 && monthlyPoints.some((p) => p.subscribers >= subGoal);

  return (
    <div data-testid="growth-chart" style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)" }}
      className="mt-6 rounded-2xl p-6 shadow-sm ring-1 ring-ivory-400/60">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="flex items-center gap-2 font-display text-xl text-ink">
            <TrendingUp className="h-5 w-5 text-gold-deep" /> Cumulative Growth
          </p>
          <p className="mt-0.5 text-sm text-ink-soft">Revenue and readers over time — switch to Monthly to track goals.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full bg-ivory-50 p-1 ring-1 ring-ivory-400/60" data-testid="growth-view-toggle">
            {VIEWS.map((v) => (
              <button key={v.id} data-testid={`growth-view-${v.id}`} onClick={() => setView(v.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${view === v.id ? "bg-gold-deep text-ivory-50" : "text-ink-soft hover:text-ink"}`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full bg-ivory-50 p-1 ring-1 ring-ivory-400/60" data-testid="growth-horizon-toggle">
            {HORIZONS.map((h) => (
              <button key={h.id} data-testid={`growth-horizon-${h.id}`} onClick={() => setHorizon(h.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${horizon === h.id ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}>
                {h.label}
              </button>
            ))}
          </div>
          <button data-testid="growth-goals-edit" onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full bg-ivory-50 px-3 py-1.5 text-xs text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink">
            <Target className="h-3.5 w-3.5" /> Goals
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl bg-ivory-50 p-4 ring-1 ring-ivory-400/60 sm:flex-row sm:items-end" data-testid="growth-goals-editor">
          <label className="flex-1 text-xs text-ink-soft">
            Monthly revenue goal ($)
            <input data-testid="goal-revenue-input" type="number" min="0" value={draftRevenue} onChange={(e) => setDraftRevenue(e.target.value)} placeholder="e.g. 2000"
              className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2 text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
          </label>
          <label className="flex-1 text-xs text-ink-soft">
            Active subscriber goal
            <input data-testid="goal-subs-input" type="number" min="0" value={draftSubs} onChange={(e) => setDraftSubs(e.target.value)} placeholder="e.g. 50"
              className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2 text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
          </label>
          <div className="flex gap-2">
            <button data-testid="goal-save" onClick={saveGoals} disabled={savingGoals}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs text-ivory-50 disabled:opacity-50">
              {savingGoals ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
            <button data-testid="goal-cancel" onClick={cancelGoals}
              className="inline-flex items-center gap-1.5 rounded-full bg-ivory-50 px-3 py-2 text-xs text-ink-soft ring-1 ring-ivory-400/60">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {goalError && <p className="text-xs text-terracotta sm:self-center" data-testid="goal-error">{goalError}</p>}
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink-muted" /></div>
      ) : error ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-sm text-ink-muted" data-testid="growth-error">
          <p>{error}</p>
          {expired ? (
            <button onClick={() => onAuthError?.()} data-testid="growth-signin" className="rounded-full bg-ink px-4 py-1.5 text-xs text-ivory-50">Sign in again</button>
          ) : (
            <button onClick={() => load(horizon)} data-testid="growth-retry" className="rounded-full bg-ink px-4 py-1.5 text-xs text-ivory-50">Try again</button>
          )}
        </div>
      ) : raw.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-ink-muted" data-testid="growth-empty">
          No activity yet — this chart fills as orders, subscribers, and readers arrive.
        </div>
      ) : view === "cumulative" ? (
        <div className="mt-5 h-64 sm:h-80" data-testid="growth-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cumPoints} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <defs>
                {SERIES.map((s) => (
                  <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={s.fill} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="#00000008" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a8070" }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis yAxisId="money" tick={{ fontSize: 11, fill: "#8a8070" }} tickLine={false} axisLine={false} width={52}
                tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
              <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11, fill: "#A98B63" }} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
              <Tooltip content={<CumulativeTooltip />} />
              {SERIES.map((s) => (
                <Area key={s.key} yAxisId={s.axis} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                  strokeWidth={2.4} fill={`url(#grad-${s.key})`} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-5 h-64 sm:h-80" data-testid="growth-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyPoints} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-revenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={REVENUE_COLOR} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={REVENUE_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#00000008" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a8070" }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis yAxisId="money" tick={{ fontSize: 11, fill: "#8a8070" }} tickLine={false} axisLine={false} width={52}
                tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
              <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11, fill: SUBS_COLOR }} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
              <Tooltip content={<MonthlyTooltip />} />
              <Area yAxisId="money" type="monotone" dataKey="revenue" name="Revenue this month" stroke={REVENUE_COLOR}
                strokeWidth={2.4} fill="url(#grad-revenue)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              <Line yAxisId="count" type="monotone" dataKey="subscribers" name="Active subscribers" stroke={SUBS_COLOR}
                strokeWidth={2.4} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              {revGoalDollars > 0 && (
                <ReferenceLine yAxisId="money" y={revGoalDollars} ifOverflow="extendDomain" stroke={REVENUE_COLOR} strokeDasharray="6 5" strokeWidth={1.5}
                  label={{ value: revReached ? "Revenue goal reached ✓" : `Revenue goal ${usdFromDollars(revGoalDollars)}`, position: "insideTopLeft", fill: REVENUE_COLOR, fontSize: 11, fontWeight: 600 }} />
              )}
              {subGoal > 0 && (
                <ReferenceLine yAxisId="count" y={subGoal} ifOverflow="extendDomain" stroke={SUBS_COLOR} strokeDasharray="6 5" strokeWidth={1.5}
                  label={{ value: subReached ? "Subscriber goal reached ✓" : `Subscriber goal ${subGoal}`, position: "insideBottomRight", fill: SUBS_COLOR, fontSize: 11, fontWeight: 600 }} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "cumulative" ? (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="growth-legend">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-2 text-xs text-ink-soft">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              {s.name}
              {data?.totals && (
                <span className="font-medium text-ink">{s.money ? usd(data.totals[`${s.key}_cents`]) : data.totals.contacts}</span>
              )}
            </span>
          ))}
          <span className="ml-auto text-[11px] text-ink-muted">Revenue on left axis · contacts on right</span>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="growth-legend">
          <span className="flex items-center gap-2 text-xs text-ink-soft">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: REVENUE_COLOR }} /> Revenue this month
          </span>
          <span className="flex items-center gap-2 text-xs text-ink-soft">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: SUBS_COLOR }} /> Active subscribers
          </span>
          {(revGoalDollars > 0 || subGoal > 0) && (
            <span className="ml-auto text-[11px] text-ink-muted" data-testid="growth-goal-note">
              Dashed lines are your goals{revReached || subReached ? " — a goal has been reached" : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
